import { createContext, FunctionComponent, useCallback, useEffect, useMemo, useState } from 'react';
import {
  AD_PAUSE,
  AD_PLAY,
  MEDIA_ENDED,
  MEDIA_PAUSE,
  MEDIA_PLAY,
  UnifiedPlayerEvents,
} from '@tv2-player/unifiedplayer/src/core/events';
import EventEmitter from 'eventemitter3';
import { Subscription } from 'rxjs';
import URLSearchParamsPonyfill from 'core-js-pure/actual/url-search-params';
import ArrayFromPonyfill from 'core-js-pure/actual/array/from';
import { AdUnit, Targeting, useDfpSlot } from './hooks';
import styles from './StaticCommercial.module.css';

export const StaticCommercialContext = createContext<{ ppid?: string } | undefined>(undefined);

type AdOverlayProps = {
  ad: AdUnit;
  shouldShow: boolean;
  player?: PlayerProps;
  targeting?: Targeting;
};

export type PlayerProps = {
  on:
    | EventEmitter['on'] // web
    | ((event: UnifiedPlayerEvents, cb: (...args: any[]) => any) => Subscription); // smart tv
  off?: EventEmitter['off'];
  ended?: boolean;
};

type StaticCommercialProps = {
  player: PlayerProps;
  ad: AdUnit;
  ppid?: string;
  targeting?: Targeting;
  hide?: boolean;
};

export const AdOverlay: FunctionComponent<AdOverlayProps> = ({
  ad,
  shouldShow,
  player,
  targeting,
}) => {
  const { readyToShow, tearDownPubads } = useDfpSlot(ad, shouldShow, player, targeting);

  useEffect(() => {
    return () => {
      tearDownPubads();
    };
  }, [tearDownPubads]);

  return (
    <div
      className={`${styles.commercialOverlay} ${shouldShow && readyToShow ? styles.visible : ''}`}
    >
      <div className={styles.commercialWrapper}>
        <div key={ad.divId} className={styles.commercial} id={ad.divId} />
      </div>
      <small className={styles.commercialSmallText}>annonce</small>
    </div>
  );
};

export const StaticCommercial: FunctionComponent<StaticCommercialProps> = ({
  player,
  ad,
  ppid,
  targeting,
  hide = false,
}) => {
  const [shouldShow, setShouldShow] = useState(false);
  const memoizedContextValue = useMemo(() => ({ ppid }), [ppid]);
  const onShouldNotShow = useCallback(() => {
    setShouldShow(false);
  }, []);

  const onShouldShow = useCallback(() => {
    setShouldShow(!hide);
  }, [hide]);

  useEffect(() => {
    const subscriptions = [
      player?.on(MEDIA_PAUSE, onShouldShow),
      player?.on(MEDIA_ENDED, onShouldNotShow),
      player?.on(MEDIA_PLAY, onShouldNotShow),
      player?.on(AD_PAUSE, onShouldNotShow),
      player?.on(AD_PLAY, onShouldNotShow),
    ] as Subscription[];

    return () => {
      if (player?.off) {
        // web
        player?.off(MEDIA_PAUSE, onShouldShow);
        player?.off(MEDIA_ENDED, onShouldNotShow);
        player?.off(MEDIA_PLAY, onShouldNotShow);
        player?.off(AD_PAUSE, onShouldNotShow);
        player?.off(AD_PLAY, onShouldNotShow);
      } else {
        // smart tv
        subscriptions.forEach(s => s?.unsubscribe?.());
      }
    };
  }, [onShouldShow, onShouldNotShow, player]);

  return (
    <StaticCommercialContext.Provider value={memoizedContextValue}>
      <AdOverlay ad={ad} shouldShow={shouldShow && !hide} player={player} targeting={targeting} />
    </StaticCommercialContext.Provider>
  );
};
export function getTargeting(prerollAdUrl?: string): Targeting {
  if (prerollAdUrl == null) {
    return [];
  }

  const searchParams = new URL(prerollAdUrl).search;
  const exclude = ['impl', 'gdfp_req', 'env', 'output', 'unviewed_position_start', 'ad_rule'];
  const allowedParams = ['sid', 'cust_params'];

  const params = allowedParams
    .map(param => [param, new URLSearchParamsPonyfill(searchParams).get(param) ?? ''])
    .map(([key, param]) => {
      const entries = ArrayFromPonyfill(new URLSearchParamsPonyfill(param).entries());
      if (entries.length === 1) {
        return [[key, param]] as [string, string][];
      }

      return entries;
    })
    .flat();

  return params.filter(([key]) => !exclude.includes(key));
}
